import React, { useMemo } from 'react';
import { GridItem, Text } from '@stockx/ui';
import {
  getFooterStyleByType,
  getPricelineData,
  getProductDisplayData,
  getXpressShipFooter,
  Priceline,
  ProductTile,
} from '@app/catalog';
import { useFormatDate, useFormatRelativeDate } from '@app/datetime';
import CalendarTile from '@drc/components/Catalog/Product/CalendarTile';
import { Events } from '@app/analytics';
import { useCurrency } from '@app/currency';
import { useTranslation } from 'react-i18next';
import type { LegacyProduct } from '@app/types';
import { useExperiment } from '@app/experiments';
import { EXPERIMENTS } from '@app/config';

const getFooterType = (footnotesType: string | undefined) => {
  if (footnotesType === 'NUMBER_SOLD') {
    return 'deadstock_sold';
  }

  if (footnotesType === 'YOUR_BID') {
    return 'your_bid';
  }

  if (footnotesType === 'YOUR_ASK') {
    return 'your_ask';
  }

  if (footnotesType === 'HIDDEN_GEM') {
    return 'trending_deal';
  }

  return 'last_sale';
};

type DynamicProductRowItemProps = {
  product: LegacyProduct;
  isCalendar: boolean;
  browsePageSelector: any;
  index: number;
  trackingEvent: string;
  screenName: string;
  swooshName: string;
  footnotesType: string;
};

const DynamicProductRowItem = ({
  product,
  isCalendar,
  browsePageSelector,
  index,
  trackingEvent,
  screenName,
  swooshName,
  footnotesType,
}: DynamicProductRowItemProps) => {
  const { t } = useTranslation();
  const formatDate = useFormatDate(false);
  const formatTime = useFormatRelativeDate();
  const { currency } = useCurrency();
  const footerType = getFooterType(footnotesType);

  const XpressShipFooter = getXpressShipFooter(product);

  const DynamicFooter = useMemo(
    () => !XpressShipFooter && getFooterStyleByType(footerType),
    [footerType, XpressShipFooter],
  );

  const isPromotedProductEnabled =
    useExperiment(EXPERIMENTS.ab_sponsored_rfy_tile_web) === 'true';

  const isPromotedProduct = product?.isAdv && isPromotedProductEnabled;

  const displayData = getProductDisplayData(
    product,
    footerType,
    t,
    formatDate,
    formatTime,
    currency,
  );

  const pricelineData = getPricelineData(
    product,
    t,
    currency,
    browsePageSelector,
    undefined,
    footerType === 'trending_deal', // includeStrikethroughAmount true if footerType is 'trending_deal'
  );

  if (!product.uuid) {
    return null;
  }

  if (isCalendar) {
    return (
      <GridItem as="li" key={product.uuid} data-component="calendar-tile">
        <CalendarTile
          key={product.uuid}
          tracking={Events.ReleaseCalendarClick}
          product={product}
          screenName={screenName}
          swooshName="Release Calendar"
          position={index + 1}
        />
      </GridItem>
    );
  }

  return (
    <GridItem
      as="li"
      role="listitem"
      key={product.uuid}
      data-component="product-tile"
    >
      <ProductTile
        index={index}
        tracking={trackingEvent}
        isPromotedProduct={isPromotedProduct}
        product={product}
        screenName={screenName}
        swooshName={swooshName}
      >
        <Priceline
          arial-label={`${pricelineData.label} ${pricelineData.amount}`}
          label={pricelineData.label}
          amount={pricelineData.amount}
        />
        {isPromotedProduct && (
          <Text fontSize="xs" color="neutral.500">
            {/* Hardcoded size will be replaced once backend changes are complete */}
            {t('pdp_size_label')} 11 (Men's)
          </Text>
        )}
        {DynamicFooter && !isPromotedProduct && (
          <DynamicFooter
            displayData={displayData}
            type={footerType}
            product={product}
            strikethroughAmount={
              footerType === 'trending_deal'
                ? pricelineData?.strikethroughAmount
                : undefined
            }
          />
        )}
        {XpressShipFooter && <XpressShipFooter />}
      </ProductTile>
    </GridItem>
  );
};

export default DynamicProductRowItem;
